/**
 * WorkspaceTransitionOverlay — the full-screen, interaction-blocking
 * organization-change announcement, plus its post-reload continuation.
 * Owner directive 2026-08-19 established the sealed blocking flow ("not
 * allowing the user to touch anything else"); 2026-08-20 revised the pacing
 * and look: faster (1.2s floor, overlapped with the real work — see
 * workspaceTransition.ts), fully OPAQUE warm-black scrim (no page
 * bleed-through, no focus box), light wordmark, no workspace dot, and a
 * short fade-out when the destination resolves.
 *
 * Mounted ONCE at app level (WorkspaceTransitionHost). Two triggers:
 *   - a live transition begun via beginWorkspaceTransition (any switch
 *     path: foreign-thread open, WorkspacePill, login card, Organization
 *     page, onboarding launch) — phase 'switching' / 'error';
 *   - the BOOT continuation: after the hard navigation, the sessionStorage
 *     marker re-raises the same overlay until the destination workspace
 *     actually RESOLVES (onActiveWorkspaceResolved), so no pre-switch or
 *     mixed-org frame can ever reach the user's eyes ("display information
 *     before the official switch" trust break). A settled-without-account
 *     signal (guest/unmigrated boots) releases promptly; a watchdog covers
 *     genuinely unresolved gates. public/index.html's splash reads the same
 *     marker so the reload gap shows this exact look, not the default
 *     splash.
 *
 * Blocking mechanics per house doctrine: body portal at BODY_PORTAL_Z_INDEX
 * (never a bespoke number), data-prest0n-portal (portalClickGuard),
 * reference-counted body scroll lock, a focus sink + document-level capture
 * swallowing every key, and NO dismiss affordance — this overlay ends only
 * by navigation, resolution, or its own timers. Deliberately non-dismissible
 * (a first for this repo): the switch is short, and a half-switched surface
 * is the exact trust break this exists to prevent.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
// From the layering ladder, never the chat's styles.ts (PR-S, 2026-09-22).
import { BODY_PORTAL_Z_INDEX } from '../styles/layers';
import { Spinner } from './common/Spinner';
import BrandWordmark from './BrandWordmark';
// What it does and says (PV5-8:D part 1, 2026-10-13): the hooks hold the switch's lifecycle and its seal, the words file
// its words; this file only draws them.
import {
  SWITCH_FADE_MS,
  useSwitchSeal,
  useWorkspaceSwitchView,
  type WorkspaceSwitchView,
} from '../hooks/workspaceSwitch/useWorkspaceSwitch';
import {
  SWITCH_WORDS,
  openingThread,
  organizationShown,
  usageBelongsTo,
} from './workspaceSwitch/workspaceSwitchWords';
// The door (PV5-8:D part 2, 2026-10-13): where the new design's `splash` shows, the switch is drawn in the new design,
// from the same hooks and words, in the visitor's light. Both ride the first download: nothing waits on a chunk.
import { PavilionWorkspaceSwitch } from '../design/pavilion/switch/PavilionWorkspaceSwitch';
import { showsNewSwitch, switchLight } from '../design/pavilion/switch/switchDoor';

const overlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Scrim = styled.div<{ $leaving?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${BODY_PORTAL_Z_INDEX};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /* Fully opaque (2026-08-20): the page must never bleed through — the
     94%-translucent scrim read as "a box floating over the page". */
  background: #100f0c;
  animation: ${overlayIn} 0.24s ease-out;
  opacity: ${(p) => (p.$leaving ? 0 : 1)};
  transition: opacity ${SWITCH_FADE_MS}ms ease;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  max-width: min(440px, calc(100vw - 48px));
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--brand-canvas);
  animation: ${cardIn} 0.32s cubic-bezier(0.16, 1, 0.3, 1);
  /* The card is a programmatic focus sink (tabIndex=-1) — the app-global
     gold :focus outline must not paint a box on it (owner 2026-08-20). */
  &:focus {
    outline: none;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: lowercase;
  color: rgba(250, 250, 248, 0.55);
`;

const OrgLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
`;

const OrgName = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.4rem, 4vw, 1.9rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--brand-canvas);
  overflow-wrap: anywhere;
`;

const ThreadLine = styled.div`
  font-size: 0.92rem;
  color: rgba(250, 250, 248, 0.82);
  overflow-wrap: anywhere;
`;

const UsageLine = styled.div`
  font-size: 0.78rem;
  color: rgba(198, 167, 94, 0.9);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 999px;
  padding: 4px 12px;
`;

const ErrorLine = styled.div`
  font-size: 0.92rem;
  color: rgba(250, 250, 248, 0.9);
`;

const OverlayBody: React.FC<{ view: WorkspaceSwitchView; leaving?: boolean }> = ({ view, leaving }) => {
  const sinkRef = useSwitchSeal();
  const orgDisplay = organizationShown(view.label);
  return (
    <Scrim
      data-workspace-transition-overlay=""
      data-prest0n-portal="true"
      role="status"
      aria-live="polite"
      aria-busy={view.kind !== 'error'}
      $leaving={leaving}
    >
      <Card ref={sinkRef} tabIndex={-1}>
        <BrandWordmark nonNavigating tone="onDark" />
        {view.kind === 'error' ? (
          <>
            <Eyebrow>{SWITCH_WORDS.failedTitle}</Eyebrow>
            <ErrorLine data-workspace-transition-error="">
              {SWITCH_WORDS.failedLine}
            </ErrorLine>
          </>
        ) : (
          <>
            <Eyebrow>{view.kind === 'arriving' ? SWITCH_WORDS.arriving : SWITCH_WORDS.switching}</Eyebrow>
            <OrgLine>
              <OrgName data-workspace-transition-org="">{orgDisplay}</OrgName>
            </OrgLine>
            {view.threadTitle && (
              <ThreadLine data-workspace-transition-thread="">
                {openingThread(view.threadTitle)}
              </ThreadLine>
            )}
            <UsageLine data-workspace-transition-usage="">
              {usageBelongsTo(orgDisplay)}
            </UsageLine>
            {/* The working mark in its night inks (D-71); the boot splash in public/index.html draws its twin, in
                phase, so the reload gap and this overlay are one continuous screen. */}
            <Spinner $onDark />
          </>
        )}
      </Card>
    </Scrim>
  );
};

/**
 * App-level host: renders the overlay for live transitions AND the boot
 * continuation. Mount exactly once (App.tsx).
 */
export const WorkspaceTransitionHost: React.FC = () => {
  const shown = useWorkspaceSwitchView();
  if (!shown) return null;
  return createPortal(
    showsNewSwitch()
      ? <PavilionWorkspaceSwitch view={shown.view} leaving={shown.leaving} light={switchLight()} />
      : <OverlayBody view={shown.view} leaving={shown.leaving} />,
    shown.portalTarget,
  );
};

/**
 * The announcement itself, for the working mark's fixtures (scripts/fixtures/working-mark): the mark is measured on
 * the real overlay. The app mounts it only through the host above.
 */
export { OverlayBody as WorkspaceTransitionOverlayView };
export type { WorkspaceSwitchView as WorkspaceTransitionView };

export default WorkspaceTransitionHost;
